import type { Position } from "../types/grid.js";
import type { GridMap } from "./GridMap.js";

/**
 * Returns true if there is an unobstructed line of sight between `from` and
 * `to` on the given grid.
 *
 * Algorithm: Amanatides-Woo DDA ray-cast.
 * The ray is parameterized so that t=0 is the center of `from` and t=1 is the
 * center of `to`. The algorithm steps cell by cell and checks each cell-edge
 * the ray crosses for a wall flag. Solid intermediate cells also block LOS.
 * Diagonal corner crossings (tMaxX === tMaxZ) check both edges.
 *
 * Callers: enemy sight cones, ranged-attack validity, spell targeting,
 * passive Perception for secret doors, trap detection.
 */
export function hasLineOfSight(grid: GridMap, from: Position, to: Position): boolean {
  if (from.x === to.x && from.z === to.z) return true;

  const dx = to.x - from.x;
  const dz = to.z - from.z;
  const stepX = dx > 0 ? 1 : -1;
  const stepZ = dz > 0 ? 1 : -1;
  const absDx = Math.abs(dx);
  const absDz = Math.abs(dz);

  // t when the ray first crosses a vertical (X) boundary, measured from t=0.
  // Starting from the cell center (0.5 inside the cell), the first boundary
  // is 0.5 units away in the direction of travel, divided by the per-axis speed.
  let tMaxX = dx !== 0 ? 0.5 / absDx : Infinity;
  let tMaxZ = dz !== 0 ? 0.5 / absDz : Infinity;
  // Time to traverse one full cell in each axis direction.
  const tDeltaX = dx !== 0 ? 1 / absDx : Infinity;
  const tDeltaZ = dz !== 0 ? 1 / absDz : Infinity;

  let cx = from.x;
  let cz = from.z;

  for (;;) {
    const cell = grid.getCell({ x: cx, z: cz });
    if (!cell) return false; // outside map bounds

    if (tMaxX === tMaxZ) {
      // Diagonal corner: ray passes through the shared corner of four cells.
      // Block only when BOTH the X-axis and Z-axis walls are present — a single
      // wall at a corner is an "open corner" the ray can squeeze through, and
      // checking only one wall would break symmetry.
      if (tMaxX > 1.0) break;
      const wallX = stepX > 0 ? cell.edges.east : cell.edges.west;
      const wallZ = stepZ > 0 ? cell.edges.south : cell.edges.north;
      if (wallX && wallZ) return false;
      cx += stepX;
      cz += stepZ;
      tMaxX += tDeltaX;
      tMaxZ += tDeltaZ;
    } else if (tMaxX < tMaxZ) {
      // Ray crosses a vertical (X) boundary next.
      if (tMaxX > 1.0) break;
      if (stepX > 0 ? cell.edges.east : cell.edges.west) return false;
      cx += stepX;
      tMaxX += tDeltaX;
    } else {
      // Ray crosses a horizontal (Z) boundary next.
      if (tMaxZ > 1.0) break;
      if (stepZ > 0 ? cell.edges.south : cell.edges.north) return false;
      cz += stepZ;
      tMaxZ += tDeltaZ;
    }

    // Reached the target — clear path.
    if (cx === to.x && cz === to.z) break;

    // Solid intermediate cells (wall tiles) block LOS.
    const mid = grid.getCell({ x: cx, z: cz });
    if (!mid || mid.solid) return false;
  }

  return true;
}
